import { devOpsAjax } from "./Common/Ajax";
import JSZip from "jszip";
import {
  AdoBranch,
  ApprovalLinkData,
  BuildTimeline,
  BuildTimelineRecord,
  ConnectionData,
  ExperimentType,
  ExtensionDetailsType,
  ExtensionFolder,
  ExtensionPipelineIds,
  ExtractedArtifactFile,
  FileCommit,
  LinkTypes,
  PipelineArtifactDownloadResult,
  PipelineConfigurationPr,
  PipelineRun,
  PipelineRunArtifact,
  PipelineRunArtifactsResponse,
  PipelineRunUserData,
  PipelineStatus,
  PullRequest,
  PullRequestChange,
} from "../Types";
import {
  approvalServiceStep,
  AzureDevOpsConfig,
  ev2ApprovalLinkStep,
  ev2ReleaseLinkStep,
  ev2ReleaseLinkStepEv2RaDeploy,
  requestApprovedStep,
} from "../Shared/AzureDevopsConfig";
import {
  PIPELINE_CONFIG_PR_KEY,
  EXTENSION_UPDATE_KEY,
  REPO_ID,
} from "../Shared/shared.consts";
import {
  bytesToBase64,
  filterExtensionFiles,
  filterExtensionFolders,
  isLikelyTextContent,
} from "../Shared/shared.utils";

const { organization, project, repository } = AzureDevOpsConfig;
// we don't want to use the cache because a lot of endpoints require
// refreshes to ensure fresh data is being pulled
const devOpsAjaxNoCache = <T>(url: string, settings?: RequestInit) => {
  return devOpsAjax<T>(url, settings, true, true);
};

// POST Endpoints

export async function postRolloutDetails(pipelineId: number, params: any) {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;
  const body = { resources: {}, templateParameters: params };
  const response = await devOpsAjaxNoCache(apiUrl, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return response;
}

export async function createAdoBranch(
  newBranchName: string,
  sourceBranch: string,
) {
  const sourceRef = sourceBranch.startsWith("refs/heads/")
    ? sourceBranch
    : `refs/heads/${sourceBranch}`;
  const newBranchRef = `refs/heads/${newBranchName}`;

  // 1. Get the latest commit SHA of the source branch
  const branches = await fetchAdoBranches();
  const newBranchExists = (branches || []).some(
    (r: any) =>
      r.name.trim().toLowerCase() === newBranchRef.trim().toLowerCase(),
  );

  // skip branch creation if branch already exists
  if (newBranchExists) {
    return;
  }

  const sourceRefObj = (branches || []).find(
    (r: any) => r.name.trim().toLowerCase() === sourceRef.trim().toLowerCase(),
  );

  if (!sourceRefObj) {
    throw new Error(
      `Source branch ${sourceBranch} not found. Available refs: ${branches
        .map((r: any) => r.name)
        .join(", ")}`,
    );
  }

  const sourceCommitId = sourceRefObj.objectId;

  const createRefUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/refs?api-version=7.1-preview.1`;
  const body = [
    {
      name: newBranchRef,
      oldObjectId: "0000000000000000000000000000000000000000",
      newObjectId: sourceCommitId,
    },
  ];

  const response = await devOpsAjaxNoCache(createRefUrl, {
    method: "POST",
    body: JSON.stringify(body),
  });

  return response;
}

export async function pushToAdoBranch({
  branchName,
  commitMessage,
  changes,
}: {
  branchName: string;
  commitMessage: string;
  changes: PullRequestChange[];
}) {
  const pushUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/pushes?api-version=7.1`;
  const refName = branchName.startsWith("refs/heads/")
    ? branchName
    : `refs/heads/${branchName}`;

  // 1. Get the latest commit SHA of the source branch
  const branches = await fetchAdoBranches();
  // Use a case-insensitive, trimmed match
  const branchRef = (branches || []).find(
    (r: any) => r.name.trim().toLowerCase() === refName.trim().toLowerCase(),
  );

  if (!branchRef) {
    throw new Error(
      `Branch ${branchName} not found. Available refs: ${branches
        .map((r: any) => r.name)
        .join(", ")}`,
    );
  }

  const oldObjectId = branchRef.objectId;

  // Check existence for each file and build the changes array
  const processedChanges = await Promise.all(
    changes.map(async (change) => {
      // Check if the file exists when adding a new file, this can happen a pr
      // was abandoned but the branch still exists
      if (
        (change.changeType === "add" || change.changeType === "rename") &&
        change.itemPath
      ) {
        const fileExists = await checkAdoFileExists(
          change.itemPath,
          branchName,
        );
        change.changeType = fileExists ? "edit" : change.changeType;
      }

      return {
        ...getChangeByType(change),
      };
    }),
  );

  // Prepare the push body
  const pushBody = {
    refUpdates: [
      {
        name: refName,
        oldObjectId,
      },
    ],
    commits: [
      {
        comment: commitMessage,
        changes: processedChanges,
      },
    ],
  };
  const response = await devOpsAjaxNoCache(pushUrl, {
    method: "POST",
    body: JSON.stringify(pushBody),
  });
  return response;
}

const getChangeByType = (change: PullRequestChange) => {
  switch (change.changeType) {
    case "add":
    case "edit":
      return {
        changeType: change.changeType,
        item: { path: change.itemPath },
        newContent: {
          content: change.newContent || "",
          contentType: change.contentType || "rawtext",
        },
      };
    case "rename":
      return {
        changeType: change.changeType,
        item: { path: change.itemPath },
        sourceServerItem: change.sourceServerItem,
      };
    case "delete":
      return {
        changeType: change.changeType,
        item: { path: change.itemPath },
      };
    default:
      throw new Error(`Unsupported change type: ${change.changeType}`);
  }
};

export async function createAdoPullRequest({
  sourceBranch,
  targetBranch,
  title,
  description,
}: {
  sourceBranch: string;
  targetBranch: string;
  title: string;
  description: string;
}) {
  const sourceRefName = sourceBranch.startsWith("refs/heads/")
    ? sourceBranch
    : `refs/heads/${sourceBranch}`;
  const targetRefName = targetBranch.startsWith("refs/heads/")
    ? targetBranch
    : `refs/heads/${targetBranch}`;
  const prBody = {
    sourceRefName,
    targetRefName,
    title,
    description: description || "",
  };
  const prUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/pullrequests?api-version=7.1`;
  const response = await devOpsAjaxNoCache(prUrl, {
    method: "POST",
    body: JSON.stringify(prBody),
  });
  return response;
}

// GET Endpoints

/**
 * Checks whether the signed-in user has read access to the AzureUX-Experimentation ADO repo.
 * Returns true if the repo is reachable, false if the user is unauthorized or the repo is not found.
 */
export async function checkAdoRepoAccess(): Promise<boolean> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}?api-version=7.1`;
  try {
    const repoData = (await devOpsAjaxNoCache(apiUrl)) as any;
    return repoData?.id === REPO_ID;
  } catch {
    return false;
  }
}

export async function fetchExtensions(): Promise<ExtensionFolder[] | null> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?scopePath=/Extensions/&recursionLevel=full&$format=json&api-version=7.1-preview.1`;
  try {
    const { value: files } = await devOpsAjaxNoCache<{
      value: ExtensionFolder[];
    }>(apiUrl);
    const filteredFiles = filterExtensionFolders(files);
    // For each file (not folder), get the last commit info
    const filesWithCommitInfo = await Promise.allSettled(
      filteredFiles.map(async (file) => {
        if (!file.isFolder) {
          try {
            // Get the last commit for this file
            const lastCommit = await fetchFileLatestCommit(file.path);
            if (lastCommit) {
              return {
                ...file,
                lastModifiedBy: lastCommit.author.email,
                lastModifiedDate: lastCommit.author.date,
              };
            }
          } catch (error) {
            console.error(
              `Failed to fetch commit info for ${file.path}:`,
              error,
            );
          }
        }
        return file;
      }),
    );

    return filesWithCommitInfo
      .filter((result) => result.status === "fulfilled")
      .map(
        (result) => (result as PromiseFulfilledResult<ExtensionFolder>).value,
      );
  } catch (error) {
    console.error("Failed to fetch extensions:", error);
    return null;
  }
}

/**
 * Fetches the experiment JSON experiments for a given extension.
 * @param extensionName Name of the extension to fetch experiment jsons
 */
export async function fetchJsonExtensionExperiments(
  extensionName?: string,
  excludeCommits?: boolean,
): Promise<ExperimentType[] | null> {
  const resolveName = extensionName
    ? `/Extensions/${encodeURIComponent(extensionName)}`
    : "/Extensions";
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?scopePath=${resolveName}&recursionLevel=full&$format=json&api-version=7.1-preview.1`;

  try {
    const { value: files } = await devOpsAjaxNoCache<{
      value: ExtensionFolder[];
    }>(apiUrl);

    const filteredFiles = filterExtensionFiles(files);
    // For each file (not folder), get the last commit info
    const filesWithCommitInfo = await Promise.allSettled(
      filteredFiles.map(async (file) => {
        try {
          // Get the last commit for this file
          const [lastCommit, content] = await Promise.all([
            excludeCommits ? null : fetchFileLatestCommit(file.path),
            fetchAdoJsonContent(file),
          ]);

          if (lastCommit) {
            return {
              ...file,
              lastModifiedBy: lastCommit.author.email,
              lastModifiedDate: lastCommit.author.date,
              ...content,
            };
          } else {
            return {
              ...file,
              ...content,
            };
          }
        } catch (error) {
          console.error(`Failed to fetch commit info for ${file.path}:`, error);
        }
      }),
    );
    return filesWithCommitInfo
      .filter(
        (result) =>
          result.status === "fulfilled" &&
          !!(result as PromiseFulfilledResult<ExperimentType>).value.featureKey,
      )
      .map(
        (result) => (result as PromiseFulfilledResult<ExperimentType>).value,
      );
  } catch (error) {
    console.error("Failed to fetch extensions:", error);
    return null;
  }
}

/**
 * Fetches the extension experiments for a given extension, does not include the file content only ado metadata
 * @param extensionName Name of the extension to fetch experiments for.
 */
export async function fetchExtensionExperiments(
  extensionName?: string,
): Promise<ExtensionFolder[] | null> {
  const resolveName = extensionName ?? "";
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?scopePath=/Extensions/${encodeURIComponent(
    resolveName,
  )}&recursionLevel=full&$format=json&api-version=7.1-preview.1`;

  try {
    const { value: files } = await devOpsAjaxNoCache<{
      value: ExtensionFolder[];
    }>(apiUrl);
    return files;
  } catch (error) {
    console.error("Failed to fetch extensions:", error);
    return null;
  }
}

// Get the latest commit information for a file
export async function fetchFileLatestCommit(
  filePath: string,
): Promise<FileCommit | null> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/commits?searchCriteria.itemPath=${encodeURIComponent(
    filePath,
  )}&searchCriteria.$top=1&api-version=7.1-preview.1`;

  try {
    const { value } = await devOpsAjaxNoCache<{ value: FileCommit[] }>(apiUrl);
    return value[0];
  } catch (error) {
    console.error("Failed to fetch file commit history:", error);
    return null;
  }
}

// Experiment JSON/ExperimentType

export async function fetchExperimentsInOpenPrs(
  extensionName: string,
): Promise<ExperimentType[]> {
  const openPrExperiments: ExperimentType[] = [];
  try {
    const pullRequests = await fetchAdoPullRequests();
    const prResults = await Promise.allSettled(
      pullRequests.map(async (pullRequest) => {
        try {
          const changeEntries = await fetchAdoPullRequestChanges(
            pullRequest.pullRequestId,
          );

          const changeResults = await Promise.allSettled(
            changeEntries
              .filter((change) => {
                const path = change.item?.path || "";
                return (
                  path.startsWith(`/Extensions/${extensionName}/`) &&
                  path.endsWith(".json") &&
                  !path.includes("ConfigurationSpec_ABExperiment.json") &&
                  !path.includes("ConfigurationSpec_Template.json") &&
                  !path.includes("pipelineIds.json") &&
                  !path.includes("base.json")
                );
              })
              .map(async (change) => {
                const path = change.item?.path || "";
                const jsonContent = await fetchAdoJsonContentFromPrBranch(
                  path,
                  pullRequest,
                );
                return {
                  ...jsonContent,
                  lastModifiedBy: pullRequest.createdBy.uniqueName,
                  lastModifiedDate: pullRequest.creationDate,
                  pr: pullRequest,
                  prUrl: getPullRequestUrl(pullRequest),
                } as ExperimentType;
              }),
          );

          return changeResults
            .filter((result) => result.status === "fulfilled")
            .map(
              (result) =>
                (result as PromiseFulfilledResult<ExperimentType>).value,
            );
        } catch (error) {
          console.error(
            `Failed to process PR ${pullRequest.pullRequestId}:`,
            error,
          );
          return [];
        }
      }),
    );

    // Flatten results from all PRs
    prResults.forEach((result) => {
      if (result.status === "fulfilled") {
        openPrExperiments.push(...result.value);
      }
    });
  } catch (error) {
    console.error("Failed to fetch pull requests:", error);
    return [];
  }

  return openPrExperiments;
}

export async function fetchPipelineConfigOpenPrs(): Promise<PipelineConfigurationPr | null> {
  try {
    const pullRequests = await fetchAdoPullRequests();
    const pipelineConfigPr = pullRequests.find((pullRequest) =>
      pullRequest.sourceRefName.includes(PIPELINE_CONFIG_PR_KEY),
    );
    if (!pipelineConfigPr) {
      return null;
    }
    const changeEntries = await fetchAdoPullRequestChanges(
      pipelineConfigPr.pullRequestId,
    );
    const changeEntry = changeEntries[0];
    const jsonContent = await fetchAdoJsonContentFromPrBranch(
      changeEntry.item?.path || "",
      pipelineConfigPr,
    );
    return {
      singleStagePipelineId: jsonContent.singleStagePipelineId || null,
      unifiedPipelineId: jsonContent.unifiedPipelineId || null,
      sourceRefName: pipelineConfigPr.sourceRefName || null,
      prUrl: getPullRequestUrl(pipelineConfigPr),
      __rootExtension: jsonContent.__rootExtension || null,
    } as PipelineConfigurationPr;
  } catch (error) {
    console.error("Failed to fetch pull requests:", error);
    throw error;
  }
}

export async function fetchExtensionConfigOpenPrs(): Promise<{
  extensionDetails: ExtensionDetailsType;
  prUrl: string;
  extensionName: string;
} | null> {
  try {
    const pullRequests = await fetchAdoPullRequests();
    const pipelineConfigPr = pullRequests.find((pullRequest) =>
      pullRequest?.description?.includes(EXTENSION_UPDATE_KEY),
    );
    if (!pipelineConfigPr) {
      return null;
    }
    const changeEntries = await fetchAdoPullRequestChanges(
      pipelineConfigPr.pullRequestId,
    );
    const changeEntry = changeEntries[0];
    const jsonContent = await fetchAdoJsonContentFromPrBranch(
      changeEntry.item?.path || "",
      pipelineConfigPr,
    );

    return {
      extensionDetails: {
        displayName: jsonContent.displayName,
        secretId: jsonContent.secretId,
        assignmentUnit: jsonContent.assignmentUnit,
        environment: jsonContent.environment,
        serviceIdentifier: jsonContent.serviceIdentifier,
        serviceGroup: jsonContent.serviceGroup,
        tenantId: jsonContent.tenantId,
        subscriptionKey: jsonContent.subscriptionKey,
        applicationId: jsonContent.applicationId,
      },
      prUrl: getPullRequestUrl(pipelineConfigPr),
      extensionName: jsonContent.__extension || "",
    } as {
      extensionDetails: ExtensionDetailsType;
      prUrl: string;
      extensionName: string;
    };
  } catch (error) {
    console.error("Failed to fetch pull requests:", error);
    throw error;
  }
}

/**
 * Fetch the pipeline map for a specific extension.
 * Note: This endpoint caches the pipeline map.
 * @param extension The name of the extension.
 * @returns A promise that resolves to the pipeline map.
 */
export async function fetchExperimentPipelineMap(
  extension: string,
): Promise<ExtensionPipelineIds | null> {
  const resolvedExtension = encodeURIComponent(extension);
  const path = `Extensions/${resolvedExtension}/.pipelines/pipelineIds.json`;
  const pipelinesPath = `/Extensions/${resolvedExtension}/.pipelines/`;
  const pipelinesApiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?scopePath=${pipelinesPath}&recursionLevel=OneLevel&$format=json&api-version=7.1-preview.1`;
  const { value: pipelineContents } =
    (await devOpsAjax<{ value: ExtensionFolder[] }>(pipelinesApiUrl)) ?? [];

  const isPipelineConfigured = pipelineContents.find(({ path }) =>
    path.endsWith("pipelineIds.json"),
  );

  if (!isPipelineConfigured) {
    return null;
  }

  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?path=${encodeURIComponent(
    path,
  )}&api-version=7.1-preview.1`;
  const value = await devOpsAjax<ExtensionPipelineIds>(apiUrl);
  return {
    ...value,
  };
}

export async function fetchBaseJson(
  extension: string,
): Promise<ExtensionDetailsType | null> {
  const extensionRoot = `/Extensions/${extension}`;
  const extensionRootApiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?scopePath=${extensionRoot}&recursionLevel=OneLevel&$format=json&api-version=7.1-preview.1`;
  const { value: baseJson } =
    (await devOpsAjax<{ value: ExtensionFolder[] }>(extensionRootApiUrl)) ?? [];

  const baseConfiguration = baseJson.find(({ path }) =>
    path.endsWith("base.json"),
  );

  if (!baseConfiguration) {
    return null;
  }

  const path = baseConfiguration.path;
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?path=${encodeURIComponent(
    path,
  )}&api-version=7.1-preview.1`;
  const value = await devOpsAjax<ExtensionDetailsType>(apiUrl);

  return {
    ...value,
  };
}

export async function fetchBaseJsonAll(): Promise<ExtensionDetailsType[]> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?scopePath=/Extensions&recursionLevel=full&$format=json&api-version=7.1-preview.1`;

  try {
    const { value: files } = await devOpsAjaxNoCache<{
      value: ExtensionFolder[];
    }>(apiUrl);
    const baseJsonFiles = files.filter(({ path }) =>
      path.endsWith("base.json"),
    );
    const rawContent = await Promise.allSettled(
      baseJsonFiles.map((file) => fetchAdoJsonContent(file)),
    );
    const jsonContent = rawContent
      .map((result) => (result.status === "fulfilled" ? result.value : null))
      .filter(Boolean) as ExtensionDetailsType[];
    return jsonContent;
  } catch (error) {
    console.error("Failed to fetch all base JSON files:", error);
  }

  return [];
}

export async function fetchAdoJsonContent(
  folder: ExtensionFolder | string,
): Promise<ExperimentType> {
  const path = typeof folder === "string" ? folder : folder.path;
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?path=${encodeURIComponent(
    path,
  )}&api-version=7.1-preview.1`;
  const value = await devOpsAjaxNoCache<ExperimentType>(apiUrl, undefined);
  const splitPath = path.split("/");

  let cleanedJson = value;
  if (typeof value === "string") {
    const jsonString = (value as string).replace(/\/\/\s+.*$/gm, "");
    cleanedJson = JSON.parse(jsonString);
  }

  return {
    ...cleanedJson,
    __filename: splitPath.pop() || "unknown.json",
    __fullpath: path,
    //Extensions organized, /Extensions/<rootExtension>/<nestedExtension>
    __extension: splitPath[splitPath.length - 1],
    __rootExtension: splitPath[2],
  };
}

interface AdoContent {
  __filename: string;
  __fullpath: string;
  __raw: string | any;
}

export async function fetchAdoContent(
  folder: ExtensionFolder | string,
): Promise<AdoContent> {
  const path = typeof folder === "string" ? folder : folder.path;

  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?path=${encodeURIComponent(
    path,
  )}&api-version=7.1-preview.1`;
  const value = await devOpsAjaxNoCache<AdoContent>(apiUrl);
  return {
    __filename: path.split("/").pop() || "unknown.json",
    __fullpath: path,
    __raw: value,
  };
}

export async function fetchAdoJsonContentFromPrBranch(
  path: string,
  pullRequest: PullRequest,
): Promise<ExperimentType> {
  const branch = pullRequest.sourceRefName.replace("refs/heads/", "");
  const contentUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/items?path=${encodeURIComponent(
    path,
  )}&versionDescriptor.versionType=branch&versionDescriptor.version=${encodeURIComponent(
    branch,
  )}&api-version=7.1-preview.1`;

  const value = await devOpsAjaxNoCache<ExperimentType>(contentUrl);
  const splitPath = path.split("/");
  return {
    ...value,
    __filename: splitPath.pop() || "unknown.json",
    __fullpath: path,
    //Extensions organized, /Extensions/<rootExtension>/<nestedExtension>
    __extension: splitPath[splitPath.length - 1],
    __rootExtension: splitPath[2],
  };
}

export async function checkAdoFileExists(
  path: string,
  branch: string,
): Promise<boolean> {
  // Extract directory path and filename
  const lastSlashIndex = path.lastIndexOf("/");
  const directoryPath = path.substring(0, lastSlashIndex);

  const listUrl =
    `https://dev.azure.com/${organization}/${project}` +
    `/_apis/git/repositories/${repository}/items` +
    `?scopePath=${encodeURIComponent(directoryPath)}` +
    `&versionDescriptor.versionType=branch` +
    `&versionDescriptor.version=${encodeURIComponent(branch)}` +
    `&recursionLevel=OneLevel` +
    `&api-version=7.1-preview.1`;

  try {
    const { value: files } = await devOpsAjaxNoCache<{
      value: ExtensionFolder[];
    }>(listUrl);

    // Check if our target file exists in the list
    return files.some((file) => file.path.includes(path));
  } catch (error) {
    // If directory doesn't exist, file doesn't exist
    return false;
  }
}

// Pipeline

export async function fetchPipelineWithUserData(
  pipelineId: number,
  top: number = 50,
): Promise<PipelineRunUserData[]> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/build/builds?definitions=${pipelineId}&$top=${top}&api-version=7.1`;
  const { value } = await devOpsAjaxNoCache<{ value: PipelineRunUserData[] }>(
    apiUrl,
  );
  return value;
}

export async function fetchPipelines(
  pipelineId: number,
  top: number = 50,
): Promise<PipelineRun[]> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/pipelines/${pipelineId}/runs?$top=${top}&api-version=7.1-preview.1`;
  const { value } = await devOpsAjaxNoCache<{ value: PipelineRun[] }>(apiUrl);
  return value;
}

// Pipeline artifacts

export const PARTNER_ARTIFACT_NAME =
  "drop_Test_Generate_Partner_Artifacts_BuildJob";

export async function fetchPipelineRunArtifacts(
  pipelineId: number,
  runId: number,
): Promise<PipelineRunArtifact[]> {
  void pipelineId;
  const buildArtifactsApiUrl = `https://dev.azure.com/${organization}/${project}/_apis/build/builds/${runId}/artifacts?api-version=7.1`;

  const response =
    await devOpsAjaxNoCache<PipelineRunArtifactsResponse>(buildArtifactsApiUrl);
  return response?.value ?? [];
}

export async function fetchPipelineRunArtifactAndExtract(
  pipelineId: number,
  runId: number,
  artifactName: string = PARTNER_ARTIFACT_NAME,
): Promise<PipelineArtifactDownloadResult> {
  const textDecoder = new TextDecoder("utf-8");
  const artifacts = await fetchPipelineRunArtifacts(pipelineId, runId);
  const selectedArtifact =
    artifacts.find((artifact) => artifact.name === artifactName) ||
    artifacts[0];

  if (!selectedArtifact) {
    throw new Error(
      `No artifacts found for pipelineId=${pipelineId}, runId=${runId}`,
    );
  }

  const downloadUrl =
    selectedArtifact.signedContent?.url ||
    selectedArtifact.resource?.downloadUrl;
  if (!downloadUrl) {
    throw new Error(
      `Artifact ${selectedArtifact.name} does not provide a signed download URL`,
    );
  }

  const zipResponse = await devOpsAjax<Response>(
    downloadUrl,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/octet-stream",
      },
    },
    false,
    true,
  );

  const zipBuffer = await zipResponse.arrayBuffer();
  const zip = await JSZip.loadAsync(zipBuffer);
  const extractedFiles: ExtractedArtifactFile[] = [];

  const entries = Object.values(zip.files);
  for (const entry of entries) {
    if (entry.dir) {
      continue;
    }

    const bytes = await entry.async("uint8array");
    const isBinary = !isLikelyTextContent(bytes);
    extractedFiles.push({
      filename: entry.name.split("/").pop() || "unknown",
      path: entry.name,
      content: isBinary ? bytesToBase64(bytes) : textDecoder.decode(bytes),
      isBinary,
      contentEncoding: isBinary ? "base64" : "text",
    });
  }

  return {
    artifactName: selectedArtifact.name,
    downloadUrl,
    files: extractedFiles,
  };
}

// Pull Requests

export async function fetchAdoPullRequests(
  status: string = "active",
): Promise<PullRequest[]> {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const searchCriteria = [
    `searchCriteria.status=${status}`,
    `$top=50`, // Limit to 50 most recent PRs
    `searchCriteria.minTime=${thirtyDaysAgo.toISOString()}`, // Only recent PRs
  ].join("&");

  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/pullrequests?${searchCriteria}&api-version=7.1-preview.1`;
  const { value } = await devOpsAjaxNoCache<{ value: PullRequest[] }>(apiUrl);
  return value;
}

export async function fetchAdoPullRequestChanges(
  pullRequestId: number,
): Promise<PullRequestChange[]> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/pullRequests/${pullRequestId}/iterations/1/changes?api-version=7.1-preview.1`;
  const { changeEntries } = await devOpsAjaxNoCache<{
    changeEntries: PullRequestChange[];
  }>(apiUrl);
  return changeEntries;
}

export function getPullRequestUrl(pullRequest: PullRequest) {
  return `https://dev.azure.com/${organization}/${project}/_git/${repository}/pullrequest/${pullRequest.pullRequestId}`;
}

// Connection Data

export async function fetchAdoBranches(): Promise<AdoBranch[]> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repository}/refs?api-version=7.1-preview.1`;
  const { value } = await devOpsAjaxNoCache<{ value: AdoBranch[] }>(apiUrl);
  return value;
}

export async function fetchAdoUsername(): Promise<string> {
  const apiUrl = `https://dev.azure.com/${organization}/_apis/connectionData`;
  const value = await devOpsAjaxNoCache<ConnectionData>(apiUrl);
  const email = value?.authenticatedUser?.properties?.Account?.$value || "";
  if (email) {
    return email.split("@")[0];
  }
  return "";
}

// Links

export async function fetchExtractLinks(
  runId: number,
  requestedLinks?: LinkTypes[],
): Promise<{
  approvalLink: ApprovalLinkData | null;
  ev2PortalLink: string | null;
  logOutputs: Array<{ url: string; content: string }>;
}> {
  try {
    let approvalStatus: string | null = null;
    let approvalLink: string | null = null;
    let ev2PortalLink: string | null = null;
    let logOutputs: Array<{ url: string; content: string }> = [];
    const timeline = await fetchBuildTimeline(runId);
    const mappedRecords = timeline.records
      .filter(
        (record: BuildTimelineRecord) =>
          record.name === ev2ReleaseLinkStep ||
          record.name === ev2ReleaseLinkStepEv2RaDeploy ||
          record.name === ev2ApprovalLinkStep ||
          record.name === requestApprovedStep ||
          record.name === approvalServiceStep,
      )
      .reduce(
        (
          acc: Record<string, BuildTimelineRecord>,
          record: BuildTimelineRecord,
        ) => ({
          ...acc,
          [record.name]: record,
        }),
        {},
      );

    // get approval step status
    if (
      (!requestedLinks || requestedLinks.includes(LinkTypes.ApprovalStatus)) &&
      mappedRecords[requestApprovedStep] &&
      mappedRecords[requestApprovedStep].result === "succeeded"
    ) {
      approvalStatus = PipelineStatus.COMPLETED;
    } else if (
      mappedRecords[approvalServiceStep] &&
      mappedRecords[approvalServiceStep].result === "canceled"
    ) {
      approvalStatus = PipelineStatus.CANCELED;
    }
    // get ev2 link
    if (!requestedLinks || requestedLinks.includes(LinkTypes.Ev2)) {
      const ev2Record =
        (mappedRecords[ev2ReleaseLinkStep]?.result === "succeeded" &&
          mappedRecords[ev2ReleaseLinkStep]) ||
        (mappedRecords[ev2ReleaseLinkStepEv2RaDeploy]?.result === "succeeded" &&
          mappedRecords[ev2ReleaseLinkStepEv2RaDeploy]) ||
        null;

      if (ev2Record?.log?.url) {
        const logContent = await fetchPipelineLog(ev2Record.log.url);
        const ev2PortalLinkPattern =
          /##\[warning\]Ev2 portal link - (https:\/\/ra\.ev2portal\.azure\.net\/#\/rollouts\/[^\s]+)/gi;
        const ev2Match = ev2PortalLinkPattern.exec(logContent);
        ev2PortalLink = ev2Match?.[1] || null;
      }
    }
    // get approval link
    if (
      (!requestedLinks || requestedLinks.includes(LinkTypes.ApprovalLink)) &&
      mappedRecords[ev2ApprovalLinkStep] &&
      mappedRecords[ev2ApprovalLinkStep].result === "succeeded" &&
      mappedRecords[ev2ApprovalLinkStep].log?.url
    ) {
      const logLink = mappedRecords[ev2ApprovalLinkStep].log.url;
      const logContent = await fetchPipelineLog(logLink);
      const approvalLinkPattern =
        /https:\/\/approval\.azengsys\.com\/approvalRequest\?id=[A-F0-9]+/gi;
      const approvalMatch = logContent.match(approvalLinkPattern);
      if (approvalMatch && approvalMatch.length > 0) {
        approvalLink = approvalMatch[0];
      }
    }

    return {
      approvalLink: { approvalLink, status: approvalStatus },
      ev2PortalLink,
      logOutputs,
    };
  } catch (error) {
    console.error("Failed to extract links:", error);
    return { approvalLink: null, ev2PortalLink: null, logOutputs: [] };
  }
}

async function fetchPipelineLog(logLink: string) {
  let logContent: string = "";
  try {
    logContent = await devOpsAjaxNoCache<string>(logLink, {
      headers: {
        "Content-Type": "text/plain",
      },
    });
  } catch (error) {
    console.error(`Failed to fetch log content from ${logLink}:`, error);
    throw new Error(`Failed to fetch log content from ${logLink}: ${error}`);
  }

  return logContent;
}

export async function fetchBuildTimeline(
  buildId: number,
): Promise<BuildTimeline> {
  const apiUrl = `https://dev.azure.com/${organization}/${project}/_apis/build/builds/${buildId}/timeline?api-version=7.1`;
  const value = await devOpsAjaxNoCache<BuildTimeline>(apiUrl);
  return value;
}
